/**
 * <s-stack-it> — "Show me how to stack this" demo.
 * Three product photos sit side by side; the --stack command slides them
 * together into one layered stack (and back). Photos use multiply blending so
 * white backgrounds drop away and the pieces overlap like a real stack.
 *
 * Commands (commandfor this element's id): --stack toggles the stacked state.
 * Attributes:
 * - data-autoplay: stack once automatically when the element scrolls into view.
 * - data-stacked-message / data-unstacked-message: polite status announcements.
 * Motion is CSS-owned and respects prefers-reduced-motion in the template styles.
 */
class SStackIt extends HTMLElement {
  connectedCallback() {
    this.status = this.querySelector('[data-stack-status]');
    this.buttons = [...this.querySelectorAll('[data-stack-button]')];

    this.handleCommand = (event) => {
      if (event.command === '--stack') this.toggle();
    };
    this.handleClick = (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (!('CommandEvent' in window) && button.getAttribute('commandfor') === this.id) {
        this.handleCommand({ command: button.getAttribute('command') });
      }
    };
    this.addEventListener('command', this.handleCommand);
    this.addEventListener('click', this.handleClick);

    if (this.hasAttribute('data-autoplay') && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.autoplayTimer = setTimeout(() => this.toggle(true, true), 700);
      }, { threshold: 0.5 });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.handleCommand);
    this.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
    clearTimeout(this.autoplayTimer);
  }

  toggle(force, quiet = false) {
    const stacked = typeof force === 'boolean' ? force : !this.hasAttribute('data-stacked');
    this.toggleAttribute('data-stacked', stacked);
    this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(stacked)));
    if (this.status && !quiet) {
      this.status.textContent = stacked ? this.dataset.stackedMessage || '' : this.dataset.unstackedMessage || '';
    }
  }
}

if (!customElements.get('s-stack-it')) customElements.define('s-stack-it', SStackIt);
